import { Link } from "react-router-dom";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE } from "@/config/site";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const Layout = ({ title, slug, children }) => {
  useLenis();
  useSEO({ title: `${title} | ${SITE.name}`, description: `${title} for ${SITE.name}.`, canonical: `${SITE.url}/${slug}` });
  return (
    <div className="App bg-white">
      <Header />
      <main className="mx-auto max-w-3xl px-4 pt-32 pb-20 sm:px-6">
        <Link to="/" className="text-sm font-semibold text-emerald-700 hover:underline">← Back to home</Link>
        <h1 className="mt-4 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">{title}</h1>
        <p className="mt-3 text-sm text-slate-400">Last updated {new Date().toLocaleDateString("en-IE", { year: "numeric", month: "long" })}</p>
        <div className="prose-legal mt-8 space-y-5 text-slate-600 [&_h2]:mt-8 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_li]:ml-1 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
          {children}
          <p className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            This information is provided for general guidance only and does not constitute formal legal advice.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export function PrivacyPolicy() {
  return (
    <Layout title="Privacy Policy" slug="privacy-policy">
      <p>{SITE.name} respects your privacy. This policy explains what information we collect through our website and how we use it.</p>
      <h2>Information we collect</h2>
      <p>When you submit a quote request we collect the details you provide, such as your name, phone number, email address, property location, property details, preferred dates, selected services, any message and any photos or videos you upload.</p>
      <h2>How we use your information</h2>
      <ul>
        <li>To respond to your enquiry and prepare a quote.</li>
        <li>To arrange and deliver the cleaning or related services you request.</li>
        <li>To contact you about your booking.</li>
      </ul>
      <h2>Sharing</h2>
      <p>We do not sell your personal information. We only share details where necessary to deliver the service you requested or where required by law.</p>
      <h2>Retention</h2>
      <p>We keep enquiry information only for as long as needed to provide our services and to meet legal obligations.</p>
      <h2>Your rights</h2>
      <p>You may request access to, correction of, or deletion of your personal data by contacting us at {SITE.email}.</p>
      <h2>Contact</h2>
      <p>For any privacy questions, email {SITE.email}.</p>
    </Layout>
  );
}

export function CookiePolicy() {
  return (
    <Layout title="Cookie Policy" slug="cookie-policy">
      <p>This Cookie Policy explains how {SITE.name} uses cookies and similar technologies on this website.</p>
      <h2>What are cookies?</h2>
      <p>Cookies are small text files placed on your device to help a website function and to understand how visitors use it.</p>
      <h2>How we use cookies</h2>
      <ul>
        <li><strong>Essential cookies</strong> — required for the website to work correctly.</li>
        <li><strong>Analytics cookies</strong> — where enabled, these help us understand how visitors use the site (for example Google Analytics). These are only set if analytics is configured.</li>
      </ul>
      <h2>Managing cookies</h2>
      <p>You can control and delete cookies through your browser settings. Disabling some cookies may affect how the website functions.</p>
      <h2>Contact</h2>
      <p>Questions about cookies? Email {SITE.email}.</p>
    </Layout>
  );
}

export function TermsAndConditions() {
  return (
    <Layout title="Terms and Conditions" slug="terms-and-conditions">
      <p>These terms apply to cleaning and related services provided by {SITE.name}. By booking a service you agree to the following.</p>
      <h2>Pricing and quotes</h2>
      <ul>
        <li>Starting prices shown on this website are not final quotes.</li>
        <li>The final price depends on the size, condition and information provided about the property.</li>
        <li>Customers should disclose heavy dirt, mould, waste and difficult access when requesting a quote.</li>
        <li>Additional work is only carried out with the customer's approval.</li>
      </ul>
      <h2>Access and parking</h2>
      <p>Access to the property and any required parking must be arranged by the customer. Delays caused by lack of access may affect availability and price.</p>
      <h2>Exclusions</h2>
      <p>Specialist or hazardous cleaning, construction waste, heavy mould remediation and restoration work are excluded unless agreed separately in writing.</p>
      <h2>Deposits and cancellations</h2>
      <ul>
        <li>Deposits may be required for larger bookings.</li>
        <li>Cancellation fees may apply where a booking is cancelled at short notice.</li>
      </ul>
      <h2>Rubbish and furniture removal</h2>
      <p>Rubbish removal is subject to the type and volume of waste, and is quoted separately after we receive photos.</p>
      <h2>Rental deposits</h2>
      <p>The service is designed to prepare a property to a professional standard for inspection. We do not guarantee the return of a rental deposit, as the final decision rests with the landlord or letting agent.</p>
      <h2>Contact</h2>
      <p>For any questions about these terms, email {SITE.email}.</p>
    </Layout>
  );
}
